/* Patch 15.2 — Financial Repair Center
   =========================================
   The operations cockpit for healing the GL: lists every approved
   source document that lacks a journal, surfaces a per-row root cause,
   and offers per-row Repair / Skip actions plus bulk Repair Selected.

   Strict design (per CFO mandate):
   • Repair is a human-approved action — never auto-run.
   • Skip requires a non-empty reason captured for audit.
   • Every successful repair stamps journal_no + journal_id on the
     source document AND writes an audit_log entry. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Wrench, SkipForward, AlertCircle,
  CheckCircle2, XCircle, Hammer, Search,
} from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DOC_TYPES = [
  { id: 'invoice',       label: 'Customer Invoices',  ar: 'فواتير العملاء' },
  { id: 'supplier_bill', label: 'Supplier Bills',     ar: 'فواتير الموردين' },
  { id: 'payment',       label: 'Payments',           ar: 'المدفوعات' },
  { id: 'expense',       label: 'Expenses',           ar: 'المصروفات' },
  { id: 'payroll',       label: 'Payroll',            ar: 'الرواتب' },
];

export default function FinancialRepairCenter() {
  const [activeTab, setActiveTab] = useState('invoice');
  const [summary, setSummary]     = useState(null);
  const [report, setReport]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(new Set());
  const [busyId, setBusyId]       = useState(null);
  const [skipModal, setSkipModal] = useState(null);
  const autoSelectedRef = useRef(false);  // jump-to-first-nonempty done once

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        axiosInstance.get('/repair/summary'),
        axiosInstance.get(`/repair/forensic-report?doc_type=${activeTab}&limit=500`),
      ]);
      setSummary(sRes.data);
      setReport(rRes.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // First summary load: if the current tab is empty but another tab has
  // unposted docs, jump to the first non-empty one. Runs ONCE — never
  // overrides a subsequent manual tab choice.
  useEffect(() => {
    if (autoSelectedRef.current || !summary) return;
    autoSelectedRef.current = true;
    const currentCount = summary?.[activeTab]?.unposted ?? 0;
    if (currentCount > 0) return;
    const firstNonEmpty = DOC_TYPES.find(
      (d) => (summary?.[d.id]?.unposted ?? 0) > 0,
    );
    if (firstNonEmpty) setActiveTab(firstNonEmpty.id);
  }, [summary, activeTab]);

  const repairOne = async (docId) => {
    setBusyId(docId);
    try {
      const r = await axiosInstance.post('/repair/repair',
        { doc_type: activeTab, doc_id: docId });
      toast.success(`Repaired → ${r.data.journal_no}`);
      await loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object'
        ? (detail.error || JSON.stringify(detail))
        : (detail || e.message);
      toast.error(`Repair failed: ${msg}`);
      // Still refresh to surface _repair_last_error in root_cause
      await loadAll();
    } finally { setBusyId(null); }
  };

  const bulkRepair = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.error('Select at least one row');
      return;
    }
    if (ids.length > 100) {
      toast.error('Bulk limit is 100 documents per batch');
      return;
    }
    if (!window.confirm(
        `Repair ${ids.length} document(s)?\n\n`
      + `This will post one journal per document. The operation is `
      + `idempotent — already-posted documents will be skipped.`)) return;
    setLoading(true);
    try {
      const r = await axiosInstance.post('/repair/bulk-repair',
        { doc_type: activeTab, doc_ids: ids });
      const { succeeded, failed } = r.data;
      if (failed > 0) {
        toast.warning(`Bulk: ${succeeded} succeeded, ${failed} failed`);
      } else {
        toast.success(`Bulk: ${succeeded} repaired`);
      }
      await loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(`Bulk failed: ${typeof detail === 'object' ? JSON.stringify(detail) : (detail || e.message)}`);
    } finally { setLoading(false); }
  };

  const skipOne = async (docId, reason) => {
    setBusyId(docId);
    try {
      await axiosInstance.post('/repair/skip',
        { doc_type: activeTab, doc_id: docId, reason });
      toast.success('Marked as skipped');
      setSkipModal(null);
      await loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(`Skip failed: ${typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || e.message)}`);
    } finally { setBusyId(null); }
  };

  const toggleAll = (checked) => {
    if (!checked || !report?.rows) { setSelected(new Set()); return; }
    setSelected(new Set(report.rows.map(r => r.id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const grand = summary?.__totals || { unposted: 0, skipped: 0 };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="financial-repair-center-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Hammer className="text-amber-600" size={26} /> Financial Repair Center
          </h1>
          <p className="text-sm text-slate-600">
            Heal the General Ledger: post missing journals for approved source documents. Read-first, repair-on-approval.
          </p>
        </div>
        <button
          data-testid="repair-refresh-btn"
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="repair-summary-cards">
        <div className={`rounded-lg p-3 border-2 ${grand.unposted > 0 ? 'bg-rose-50 border-rose-300' : 'bg-emerald-50 border-emerald-300'}`}>
          <div className="text-[10px] uppercase tracking-wider opacity-70">All Documents</div>
          <div className={`text-3xl font-bold mt-1 font-mono ${grand.unposted > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
            {grand.unposted}
          </div>
          <div className="text-[10px] opacity-60">{grand.skipped} skipped</div>
        </div>
        {DOC_TYPES.map((dt) => (
          <div
            key={dt.id}
            className={`rounded-lg p-3 border cursor-pointer transition ${
              activeTab === dt.id
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white border-slate-200 hover:border-slate-400'
            }`}
            onClick={() => setActiveTab(dt.id)}
            data-testid={`tab-${dt.id}`}
          >
            <div className="text-[10px] uppercase tracking-wider opacity-70">{dt.label}</div>
            <div className="text-2xl font-bold mt-1 font-mono">
              {summary?.[dt.id]?.unposted ?? 0}
            </div>
            <div className="text-[10px] opacity-60">{summary?.[dt.id]?.skipped ?? 0} skipped</div>
          </div>
        ))}
      </div>

      {report && (
        <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <h2 className="text-sm font-bold text-slate-900">
              {DOC_TYPES.find(d => d.id === activeTab)?.label} — Unposted ({report.total_unposted})
              {report.rows_over_cap > 0 && (
                <span className="ml-2 text-xs text-slate-500">
                  showing first {report.rows.length}, {report.rows_over_cap} more capped
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                Sum: <span className="font-mono font-bold">{fmt(report.sum_amount)}</span> SAR
              </span>
              <button
                data-testid="bulk-repair-btn"
                onClick={bulkRepair}
                disabled={loading || selected.size === 0}
                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                <Wrench size={12} /> Repair Selected ({selected.size})
              </button>
            </div>
          </header>

          {report.rows?.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
              No unposted {activeTab.replace('_', ' ')}s. ✓ Sub-ledger matches GL.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="repair-rows-table">
              <thead>
                <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={selected.size > 0 && selected.size === report.rows.length}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th className="px-3 py-2">Document</th>
                  <th className="px-3 py-2">Party</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Root Cause</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => {
                  const isErr = r.root_cause.startsWith('Arithmetic')
                    || r.root_cause.startsWith('Last repair');
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`repair-row-${i}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          data-testid={`row-select-${i}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono font-medium">{r.label}</td>
                      <td className="px-3 py-2 truncate max-w-[180px]">{r.party || '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{r.date || '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(r.amount)}</td>
                      <td className="px-3 py-2">
                        <span className="bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
                          {r.status || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-md">
                        <div className={`flex items-start gap-1 text-[11px] ${isErr ? 'text-rose-700' : 'text-slate-600'}`}>
                          {isErr ? <AlertCircle size={11} className="shrink-0 mt-0.5" />
                                 : <Search size={11} className="shrink-0 mt-0.5 text-slate-400" />}
                          <span>{r.root_cause}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          data-testid={`row-repair-${i}`}
                          disabled={busyId === r.id}
                          onClick={() => repairOne(r.id)}
                          className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[10px] font-semibold rounded px-2 py-1 mr-1"
                          title="Post the missing journal"
                        >
                          {busyId === r.id ? <Loader2 size={10} className="animate-spin" /> : <Wrench size={10} />}
                          Repair
                        </button>
                        <button
                          data-testid={`row-skip-${i}`}
                          disabled={busyId === r.id}
                          onClick={() => setSkipModal(r)}
                          className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 text-[10px] font-semibold rounded px-2 py-1"
                          title="Mark as historical / skip"
                        >
                          <SkipForward size={10} /> Skip
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {skipModal && (
        <SkipModal
          row={skipModal}
          onClose={() => setSkipModal(null)}
          onConfirm={(reason) => skipOne(skipModal.id, reason)}
        />
      )}
    </div>
  );
}


function SkipModal({ row, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="skip-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <h3 className="text-base font-bold text-slate-900 mb-1">
          Skip {row.label}?
        </h3>
        <p className="text-xs text-slate-600 mb-3">
          The document will be excluded from future reconciliation
          checks. This action is audit-logged. Provide a reason.
        </p>
        <textarea
          data-testid="skip-reason-input"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          minLength={3}
          maxLength={500}
          placeholder="e.g. Historical opening balance — pre-system import"
          className="w-full border border-slate-300 rounded p-2 text-sm"
        />
        <div className="text-[10px] text-slate-500 mt-1">
          {reason.length}/500 — min 3 characters required.
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button
            data-testid="skip-cancel-btn"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="skip-confirm-btn"
            onClick={() => onConfirm(reason)}
            disabled={reason.trim().length < 3}
            className="px-3 py-1.5 text-xs rounded bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
          >
            <SkipForward size={12} /> Confirm Skip
          </button>
        </div>
      </div>
    </div>
  );
}
